import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

export interface OrganizationMember {
  uid: string
  email: string
  displayName?: string
  role: 'admin' | 'editor' | 'viewer'
  joinedAt: any
  status: 'active' | 'inactive'
}

export interface Organization {
  id: string
  name: string
  description?: string
  createdAt: any
  settings?: any
}

export interface InviteMemberRequest {
  email: string
  role: 'admin' | 'editor' | 'viewer'
}

export interface UpdateOrganizationRequest {
  name?: string
  description?: string
}

/**
 * 組織情報を取得
 */
export function useOrganization() {
  const { firebaseUser } = useAuth()

  return useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      if (!firebaseUser) throw new Error('Not authenticated')

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      console.log('📊 Fetching organization:', { orgId })

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch organization: ${response.statusText}`)
      }

      const data = await response.json()
      console.log('✅ Organization fetched:', data)
      return data as Organization
    },
    enabled: !!firebaseUser
  })
}

/**
 * 組織メンバー一覧を取得
 */
export function useOrganizationMembers() {
  const { firebaseUser } = useAuth()

  return useQuery({
    queryKey: ['organization-members'],
    queryFn: async () => {
      if (!firebaseUser) throw new Error('Not authenticated')

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      console.log('👥 Fetching organization members:', { orgId })

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/members`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch members: ${response.statusText}`)
      }

      const data = await response.json()
      console.log('✅ Members fetched:', data)
      return data.members as OrganizationMember[]
    },
    enabled: !!firebaseUser
  })
}

/**
 * 組織情報を更新
 */
export function useUpdateOrganization() {
  const { firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateOrganizationRequest) => {
      if (!firebaseUser) throw new Error('Not authenticated')

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      console.log('📝 Updating organization:', { orgId, data })

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update organization')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization'] })
      console.log('✅ Organization updated successfully')
    }
  })
}

/**
 * メンバーを招待
 */
export function useInviteMember() {
  const { firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: InviteMemberRequest) => {
      if (!firebaseUser) throw new Error('Not authenticated')

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      console.log('📧 Inviting member:', { orgId, email: data.email, role: data.role })

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/members/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to invite member')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] })
      console.log('✅ Member invited successfully')
    }
  })
}

/**
 * メンバーのロールを変更
 */
export function useUpdateMemberRole() {
  const { firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: 'admin' | 'editor' | 'viewer' }) => {
      if (!firebaseUser) throw new Error('Not authenticated')

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      console.log('🔄 Updating member role:', { orgId, memberId, role })

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/members/${memberId}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to update member role')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] })
      console.log('✅ Member role updated successfully')
    }
  })
}

/**
 * メンバーを削除
 */
export function useRemoveMember() {
  const { firebaseUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (memberId: string) => {
      if (!firebaseUser) throw new Error('Not authenticated')

      const token = await firebaseUser.getIdToken()
      const tokenResult = await firebaseUser.getIdTokenResult()
      const orgId = tokenResult.claims.orgId

      console.log('🗑️ Removing member:', { orgId, memberId })

      const response = await fetch(`${API_BASE}/v1/organizations/${orgId}/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to remove member')
      }

      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-members'] })
      console.log('✅ Member removed successfully')
    }
  })
}
